import { Navigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import PublicSiteHeader from '../../components/public/PublicSiteHeader';
import PublicFiliereDetailView from '../../components/public/PublicFiliereDetailView';
import PublicSiteFooter from '../../components/public/PublicSiteFooter';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import { getDomaineMetaBySlug } from '../../constants/academiaFiliereDomains';
import { usePublicFilieres } from '../../hooks/usePublicFilieres';
import { usePublicTarifs } from '../../hooks/usePublicTarifs';
import { usePageMeta } from '../../hooks/usePageMeta';
import { fetchFilierePlanning } from '../../services/academiaApprenantService';
import { getFiliereInscriptionAvailability } from '../../utils/filiereInscriptionAvailability';
import { findPublicFiliereByCoursSlug } from '../../utils/publicFilieres';
import type { PublicPlanningSession } from '../../utils/publicPlanningDisplay';

type Props = {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  spaceLabel?: string;
  onGoToSpace?: () => void;
};

export default function FiliereDetailPage({ isAuthenticated, isAdmin, spaceLabel, onGoToSpace }: Props) {
  const { trackSlug, filiereSlug } = useParams<{ trackSlug: string; filiereSlug: string }>();
  const { domaines, cohortes, tracks, loading, error, reload } = usePublicFilieres();

  const track = useMemo(() => {
    const fromApi = tracks.find((t) => t.slug === trackSlug);
    if (fromApi) return fromApi;
    const meta = getDomaineMetaBySlug(trackSlug, domaines);
    if (!meta) return undefined;
    return { ...meta, filieres: [] };
  }, [tracks, trackSlug, domaines]);

  const filiere = useMemo(() => {
    if (!track?.filieres?.length || !filiereSlug) return undefined;
    return findPublicFiliereByCoursSlug(track.filieres, filiereSlug);
  }, [track, filiereSlug]);

  const { categories: tarifCategories, loading: tarifsLoading } = usePublicTarifs(filiere?.id);
  const [planningSessions, setPlanningSessions] = useState<PublicPlanningSession[]>([]);
  const [planningLoading, setPlanningLoading] = useState(false);

  const availability = useMemo(
    () => getFiliereInscriptionAvailability(filiere?.id ?? '', cohortes),
    [filiere?.id, cohortes],
  );

  usePageMeta({
    title: filiere?.libelle ?? 'Formation',
    description: filiere
      ? `Programme, tarifs, cohortes et planning — ${filiere.libelle}. Inscrivez-vous en ligne à Kinshasa.`
      : undefined,
    canonicalPath:
      trackSlug && filiereSlug ? `/filieres/${trackSlug}/${filiereSlug}` : undefined,
  });

  useEffect(() => {
    if (!filiere?.id) {
      setPlanningSessions([]);
      return;
    }
    let cancelled = false;
    setPlanningLoading(true);
    void (async () => {
      try {
        const rows = await fetchFilierePlanning(filiere.id);
        if (!cancelled) {
          setPlanningSessions(Array.isArray(rows) ? (rows as PublicPlanningSession[]) : []);
        }
      } catch {
        if (!cancelled) setPlanningSessions([]);
      } finally {
        if (!cancelled) setPlanningLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filiere?.id]);

  if (!loading && !getDomaineMetaBySlug(trackSlug, domaines)) {
    return <Navigate to="/" replace />;
  }

  if (!loading && track && !filiere) {
    return <Navigate to={`/filieres/${trackSlug}`} replace />;
  }

  const domainLabel = track?.title ?? filiere?.domaine ?? 'Formation';
  const domainIcon = track?.icon ?? 'school';

  return (
    <div className="min-h-screen bg-background text-on-surface">
      <PublicSiteHeader
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        spaceLabel={spaceLabel}
        onGoToSpace={onGoToSpace}
      />

      {loading ? (
        <div className="mx-auto max-w-6xl px-4 py-16 text-center text-sm text-on-surface-variant sm:px-6">
          Chargement de la formation…
        </div>
      ) : error ? (
        <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6">
          <p className="text-sm text-on-surface-variant">Impossible de charger cette formation.</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="mt-4 rounded-public-btn bg-primary px-5 py-2.5 text-sm font-bold text-white"
          >
            Réessayer
          </button>
        </div>
      ) : filiere && track && trackSlug ? (
        <PublicFiliereDetailView
          filiere={filiere}
          trackSlug={trackSlug}
          domainLabel={domainLabel}
          domainIcon={domainIcon}
          domaineUrlImage={track.urlImage}
          availability={availability}
          cohortes={cohortes}
          planningSessions={planningSessions}
          planningLoading={planningLoading}
          pricingCategories={tarifCategories}
          pricingLoading={tarifsLoading}
        />
      ) : null}

      <PublicSiteFooter />
      <ScrollToTopButton />
    </div>
  );
}
